/**
 * @license
 * Copyright 2026 Allen Institute for Brain Science
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 */

/**
 * Pure-data helpers for hooking zarr-vectors skeleton chunks into the
 * existing neuroglancer skeleton shader machinery:
 *
 * - `buildVertexAttributeMap` constructs the
 *   `Map<name, VertexAttributeInfo>` the skeleton render layer uses to
 *   generate `prop_<name>()` shader macros.  Order matches the backend's
 *   `chunk.vertexAttributes` packing: synthesised `tangent` (vec3
 *   float32) first for any geometry kind with synthesised tangents (see
 *   {@link hasSynthesisedTangent}), then user-declared attributes in
 *   declaration order.
 *
 * - `DEFAULT_STREAMLINE_FRAGMENT_MAIN` lives in `geometry_kind.ts`
 *   alongside the per-kind capability table that references it.  This
 *   module re-exports it for callers that imported it from here before
 *   the refactor.
 *
 * Lives in its own module (separate from `skeleton_frontend.ts`) so
 * unit tests can import it under Node without dragging in WebGL-coupled
 * symbols from `src/skeleton/frontend.ts`.
 */

import type { ZarrVectorsAttributeDtype } from "#src/datasource/zarr-vectors/base.js";
import type { ZarrVectorsGeometryKind } from "#src/datasource/zarr-vectors/geometry_kind.js";
import { hasSynthesisedTangent } from "#src/datasource/zarr-vectors/geometry_kind.js";
import type { VertexAttributeInfo } from "#src/skeleton/base.js";
import type { PackedAttributeRange } from "#src/skeleton/frontend.js";
import { DataType } from "#src/util/data_type.js";

export { DEFAULT_STREAMLINE_FRAGMENT_MAIN } from "#src/datasource/zarr-vectors/geometry_kind.js";

/**
 * Build the `Map<name, VertexAttributeInfo>` the skeleton render layer
 * uses to set up texture bindings and generate `prop_<name>()` shader
 * macros.  See module docstring for the ordering contract with the
 * backend.
 */
export function buildVertexAttributeMap(parameters: {
  attributeNames: string[];
  attributeDtypes: ZarrVectorsAttributeDtype[];
  attributePropertyIds?: string[];
  geometryKind: ZarrVectorsGeometryKind;
}): Map<string, VertexAttributeInfo> {
  const map = new Map<string, VertexAttributeInfo>();
  if (hasSynthesisedTangent(parameters.geometryKind)) {
    map.set("tangent", { dataType: DataType.FLOAT32, numComponents: 3 });
  }
  // Keyed by the shader-facing identifier, not the on-disk directory name:
  // the map keys become `prop_<key>()` GLSL macros, and store names such as
  // `gene_H2-Q2` are not legal identifiers.  See `attributePropertyIds`.
  const shaderNames =
    parameters.attributePropertyIds ?? parameters.attributeNames;
  for (let i = 0; i < parameters.attributeNames.length; ++i) {
    // Every declared dtype arrives as float32 (see
    // `vertex_attribute_float.ts`), so the map is uniform and a shader written
    // against one attribute compiles against any other.
    map.set(shaderNames[i], {
      dataType: DataType.FLOAT32,
      numComponents: 1,
    });
  }
  return map;
}

/**
 * The run of `vertexAttributes` entries the render layer should hand one
 * shared texture instead of a texture unit each.
 *
 * Both zarr-vectors sources lay their attributes out the same way -- position,
 * an optional synthesised tangent, the store's own attributes, then the
 * synthesised segment column -- so the run is the store's attributes, wherever
 * the tangent puts them. They qualify because the decoder makes every one of
 * them 1-component float32.
 */
export function zvPackedAttributeRange(parameters: {
  attributeNames: readonly string[];
  geometryKind: ZarrVectorsGeometryKind;
}): PackedAttributeRange | undefined {
  const count = parameters.attributeNames.length;
  if (count === 0) return undefined;
  return {
    start: 1 + (hasSynthesisedTangent(parameters.geometryKind) ? 1 : 0),
    count,
  };
}
